import { useState } from "react";
import { useTranslation } from "react-i18next";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";

import { useGetPrivacyCentreSnippets } from "@/requests/hooks/comply";
import { MAILTO_SUPPORT } from "@/utils/const";

import { H5, P } from "@/components/Typography";
import CopyContent from "@/components/CopyContent";
import { Button } from "@/components/Button";
import Shimmer from "@/components/Shimmer";
import ComponentError from "@/components/ComponentError";
import EmailDeveloperModal from "@/components/Modal/EmailDeveloperModal";

const WebsiteInstallation = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggleModal = () => setIsOpen(isOpen => !isOpen);

  const {
    data: privacyCentreSnippetsData,
    isLoading: isPrivacyCentreSnippetsLoading,
    isError: isPrivacyCentreSnippetsError,
  } = useGetPrivacyCentreSnippets();

  const prepareSnippetsList = (
    privacyCenterSnippets?: IGetPrivacyCentreSnippetsRSData,
  ): IPrivacyCentreSnippetsStep[] => {
    let headerScriptSnippetItem: IPrivacyCentreSnippetsItem | undefined;
    if (privacyCenterSnippets?.headerScript) {
      headerScriptSnippetItem = {
        codeContent: privacyCenterSnippets?.headerScript,
        trackingId: "generic.copy-head-code",
      };
    }

    let bodyScriptSnippetItem: IPrivacyCentreSnippetsItem | undefined;
    if (privacyCenterSnippets?.bodyScript) {
      bodyScriptSnippetItem = {
        codeContent: privacyCenterSnippets?.bodyScript,
        trackingId: "generic.copy-body-code",
      };
    }

    let cookieScriptSnippetItem: IPrivacyCentreSnippetsItem | undefined;
    if (privacyCenterSnippets?.cookieScript) {
      cookieScriptSnippetItem = {
        codeContent: privacyCenterSnippets?.cookieScript,
        trackingId: "generic.copy-cookie-code",
      };
    }

    return [
      {
        title: t("comply.privacyCenter.websiteInstallation.steps.head.title"),
        description: t(
          "comply.privacyCenter.websiteInstallation.steps.head.description",
        ),
        privacyCentreSnippetsItems: headerScriptSnippetItem,
      },
      {
        title: t("comply.privacyCenter.websiteInstallation.steps.body.title"),
        description: t(
          "comply.privacyCenter.websiteInstallation.steps.body.description",
        ),
        privacyCentreSnippetsItems: bodyScriptSnippetItem,
      },
      {
        title: t("comply.privacyCenter.websiteInstallation.steps.cookie.title"),
        description: t(
          "comply.privacyCenter.websiteInstallation.steps.cookie.description",
        ),
        privacyCentreSnippetsItems: cookieScriptSnippetItem,
      },
    ] as IPrivacyCentreSnippetsStep[];
  };

  const renderLoading = () => <Shimmer height="650px" />;

  const renderSnippets = () => {
    const privacyCenterSnippetsList = prepareSnippetsList(
      privacyCentreSnippetsData,
    );

    return privacyCenterSnippetsList.map((snippetItem, i) =>
      snippetItem.privacyCentreSnippetsItems ? (
        <div key={i}>{renderSnippetsData(snippetItem, i)}</div>
      ) : (
        <div key={i}>
          <ComponentError
            message={t("comply.privacyCenter.websiteInstallation.dataError")}
            height="h-[200px]"
          />
        </div>
      ),
    );
  };

  const renderSnippetsData = (
    snippetItem: IPrivacyCentreSnippetsStep,
    step: number,
  ) => (
    <>
      <H5 className="font-bold text-secondary">
        {`${step + 1}. ${snippetItem.title}`}
      </H5>
      <P className="mb-20 font-normal">{snippetItem.description}</P>
      <div className="mb-30">
        <CopyContent
          trackingId={
            snippetItem.privacyCentreSnippetsItems.trackingId as string
          }
          content={snippetItem.privacyCentreSnippetsItems.codeContent}
          isCode={true}
        />
      </div>
    </>
  );

  const renderEmailDeveloperModal = () => (
    <EmailDeveloperModal isOpen={isOpen} closeModal={toggleModal} />
  );

  const renderSupportButton = () => (
    <div className="flex flex-col items-center gap-10 sm:flex-row sm:gap-40">
      <Button
        trackingId="support.send-to-developer"
        variant="primary"
        width="sm"
        onClick={toggleModal}>
        {t("comply.privacyCenter.websiteInstallation.buttons.email")}
      </Button>
      <Button
        as="a"
        trackingId={{ eventName: "support.mailto" }}
        variant="link"
        iconPosition="end"
        icon={faCaretRight}
        iconColor="text-secondary"
        iconClassName="w-15 h-15"
        href={MAILTO_SUPPORT}
        role="link">
        {t("comply.privacyCenter.websiteInstallation.buttons.help")}
      </Button>
    </div>
  );

  if (isPrivacyCentreSnippetsLoading) return renderLoading();

  if (isPrivacyCentreSnippetsError)
    return (
      <ComponentError
        message={t("comply.privacyCenter.websiteInstallation.error")}
      />
    );

  if (
    !privacyCentreSnippetsData?.headerScript &&
    !privacyCentreSnippetsData?.bodyScript &&
    !privacyCentreSnippetsData?.cookieScript
  )
    return (
      <ComponentError
        message={t("comply.privacyCenter.websiteInstallation.dataError")}
      />
    );

  return (
    <>
      <div className="mt-30">{renderSnippets()}</div>
      {renderSupportButton()}
      {renderEmailDeveloperModal()}
    </>
  );
};

export default WebsiteInstallation;
